import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, X, Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Soft email-verification nudge banner.
 *
 * Shown at the top of every authenticated app page when the user's email
 * isn't verified yet. We don't block UI on verification (that would hurt
 * conversion — see "verify-after-pay" funnel) but we strongly encourage
 * it because:
 *   - drip emails skip unverified users (we won't pester invalid addresses)
 *   - password reset works only if email actually delivers
 *   - support / billing receipts go to that email
 *
 * One-click resend, one-click dismiss (24h cooldown via localStorage).
 */
const DISMISS_KEY = "chillrefer_verify_banner_dismissed_at";
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h

export default function EmailVerifyBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const ts = localStorage.getItem(DISMISS_KEY);
      if (!ts) return false;
      return Date.now() - new Date(ts).getTime() < DISMISS_COOLDOWN_MS;
    } catch { return false; }
  });

  // Hide for verified users, unauth state, or recently dismissed
  if (!user || user.email_verified || dismissed) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      const token = (() => {
        try { return localStorage.getItem("chillrefer_token"); } catch { return null; }
      })();
      const res = await fetch(`${API}/api/auth/send-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success("Verification code sent to your inbox");
      navigate("/verify-email");
    } catch {
      toast.error("Couldn't send code — try again in a moment");
    }
    setResending(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch {}
  };

  return (
    <div
      data-testid="email-verify-banner"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        background: "linear-gradient(90deg, rgba(250,204,21,0.08), rgba(250,204,21,0.02))",
        border: "1px solid rgba(250,204,21,0.25)",
        borderRadius: 8,
        margin: "0 0 16px",
        fontFamily: "'Manrope', sans-serif",
      }}
    >
      <div
        style={{
          width: 32, height: 32, borderRadius: "50%",
          background: "rgba(250,204,21,0.15)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        <Mail size={15} style={{ color: "#FACC15" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: "#fafafa", margin: 0, marginBottom: 2 }}>
          One more thing — verify your email
        </p>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", margin: 0, lineHeight: 1.45 }}>
          We sent a 6-digit code to <span style={{ color: "#FACC15", fontWeight: 600 }}>{user.email}</span>.
          {" "}Verifying unlocks reply notifications, password recovery, and account security.
        </p>
      </div>
      <button
        onClick={handleResend}
        disabled={resending}
        data-testid="email-verify-banner-resend"
        style={{
          background: "#FACC15", color: "#000", border: "none",
          padding: "8px 14px", borderRadius: 6, cursor: resending ? "not-allowed" : "pointer",
          fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", gap: 6,
          flexShrink: 0,
        }}
      >
        {resending ? <Loader2 size={12} className="animate-spin" /> : null}
        {resending ? "Sending…" : "Verify now"}
      </button>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        data-testid="email-verify-banner-dismiss"
        style={{
          background: "transparent", border: "none", color: "rgba(255,255,255,0.4)",
          cursor: "pointer", padding: 4, display: "flex", alignItems: "center",
          flexShrink: 0,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
